export interface Service {
  slug: string
  title: string
  shortDescription: string
  longDescription: string
  iconName:
    | 'Flame'
    | 'Wrench'
    | 'Thermometer'
    | 'ShieldCheck'
    | 'Phone'
    | 'Droplets'
  heroImage: string
  gallery: string[]
  features: string[]
}

const IMG_BOILER_DIAGNOSTIC =
  'https://cdn.magicpatterns.com/uploads/pxBx9HU6K3suuYWkpBL1RG/WhatsApp_Image_2026-05-05_at_4.24.30_AM_(2).jpg'
const IMG_BOILER_DIAGNOSTIC_2 =
  'https://cdn.magicpatterns.com/uploads/77nZ2ujGRrRv1NXoauoB1Q/WhatsApp_Image_2026-06-01_at_2.15.28_PM_(1).jpg'
const IMG_GAS_HOB =
  'https://cdn.magicpatterns.com/uploads/5AcKKGDmPK8ztg4WqFs3eJ/WhatsApp_Image_2026-05-05_at_4.24.30_AM_(1).jpg'
const IMG_CYLINDER =
  'https://cdn.magicpatterns.com/uploads/kFLLiUNdY4ngctKLn2YBCp/WhatsApp_Image_2026-05-05_at_4.24.30_AM.jpg'
const IMG_PIPEWORK =
  'https://cdn.magicpatterns.com/uploads/5mMfjie5sDVNxdV8EiLoo3/WhatsApp_Image_2026-06-03_at_1.46.16_PM_(3).jpg'
const IMG_BOILER_INSTALL =
  'https://cdn.magicpatterns.com/uploads/cxBxNa6rdGYvefkFtxtezT/WhatsApp_Image_2026-06-04_at_3.34.11_PM.jpg'
const IMG_BOILER_REPAIR =
  'https://cdn.magicpatterns.com/uploads/9ephv8fsbZtTD25Xom5G4L/WhatsApp_Image_2026-06-04_at_3.34.51_PM.jpg'
const IMG_GAS_HOB_NEW =
  'https://cdn.magicpatterns.com/uploads/qtMnXLuNs3Kgg7Lo4if21M/WhatsApp_Image_2026-06-04_at_3.36.02_PM.jpg'
const IMG_EMERGENCY =
  'https://cdn.magicpatterns.com/uploads/rdJ6GBvzVA5utFEXmAM86P/Plumber2Btightening2Bnuts2Bon2Ba2Bgas2Bboiler2Bsystem.webp'

export const services: Service[] = [
  {
    slug: 'boiler-installation',
    title: 'Boiler Installation',
    iconName: 'Flame',
    shortDescription:
      'Energy-efficient boiler replacements and new installations by certified experts.',
    longDescription:
      'From combi to system and conventional boilers, our Gas Safe registered engineers deliver clean, code-compliant installations using top brands like Baxi, Worcester Bosch, Vaillant and Ideal. We size every system to your home, run the pipework neatly, and commission to manufacturer spec — backed by up to 10 years\u2019 warranty.',
    heroImage: IMG_BOILER_INSTALL,
    gallery: [IMG_BOILER_INSTALL, IMG_BOILER_DIAGNOSTIC, IMG_CYLINDER],
    features: [
      'Up to 10-year manufacturer warranty',
      'A-rated energy-efficient models',
      'Tidy copper pipework & magnetic filter',
      'Full system flush & commissioning',
      'Old boiler removal & disposal included',
    ],
  },
  {
    slug: 'boiler-repairs',
    title: 'Boiler Repairs',
    iconName: 'Wrench',
    shortDescription:
      'Fast diagnostics and reliable repairs to get your heating back up and running.',
    longDescription:
      'No heating or hot water? Our engineers use professional flue gas analysers to diagnose faults quickly and accurately. We carry common spares on the van so most repairs are completed on the first visit — at a fair, upfront price with no hidden call-out fees.',
    heroImage: IMG_BOILER_REPAIR,
    gallery: [IMG_BOILER_REPAIR, IMG_BOILER_DIAGNOSTIC_2, IMG_PIPEWORK],
    features: [
      'Same-day appointments where possible',
      'Fixed-fee diagnostics',
      'Genuine manufacturer parts',
      'All major brands serviced',
      '12-month parts & labour guarantee',
    ],
  },
  {
    slug: 'central-heating-systems',
    title: 'Central Heating Systems',
    iconName: 'Thermometer',
    shortDescription:
      'Complete system design, installation, and upgrades for optimal home comfort.',
    longDescription:
      'Full central heating design and installation — from new radiators and smart thermostats to unvented cylinders and full pipework replacements. We plan the layout, balance the system, and ensure every room reaches the right temperature efficiently.',
    heroImage: IMG_CYLINDER,
    gallery: [IMG_CYLINDER, IMG_PIPEWORK, IMG_BOILER_DIAGNOSTIC],
    features: [
      'Unvented cylinder installation (G3 certified)',
      'Smart thermostat & zone controls',
      'Radiator upgrades & repositioning',
      'System balancing for even heat',
      'TRVs fitted as standard',
    ],
  },
  {
    slug: 'gas-safety-certificates',
    title: 'Gas Safety Certificates',
    iconName: 'ShieldCheck',
    shortDescription:
      'CP12 landlord certificates and comprehensive safety inspections.',
    longDescription:
      'Landlords are legally required to hold a valid Gas Safety Record (CP12) for every gas appliance in their property. We carry out full inspections, tightness tests and flue gas analysis, and issue your certificate digitally the same day.',
    heroImage: IMG_BOILER_DIAGNOSTIC_2,
    gallery: [IMG_BOILER_DIAGNOSTIC_2, IMG_GAS_HOB, IMG_BOILER_DIAGNOSTIC],
    features: [
      'CP12 issued digitally same day',
      'All gas appliances checked',
      'Flue gas analysis & tightness test',
      'Landlord-compliant documentation',
      'Reminders for annual renewal',
    ],
  },
  {
    slug: 'emergency-callouts',
    title: 'Emergency Callouts',
    iconName: 'Phone',
    shortDescription:
      '24/7 rapid response for gas leaks, boiler breakdowns, and heating emergencies.',
    longDescription:
      'Gas leak, no hot water, leaking pipework? Our 24/7 emergency line connects you to a local Gas Safe engineer fast. We prioritise safety first — making the property safe before completing repairs.',
    heroImage: IMG_EMERGENCY,
    gallery: [IMG_EMERGENCY, IMG_BOILER_REPAIR, IMG_PIPEWORK],
    features: [
      '24/7 availability — including weekends',
      'Rapid response across Glasgow & Clydeside',
      'Gas leak make-safe',
      'No call-out fee for diagnosed faults',
      'Direct line to your engineer',
    ],
  },
  {
    slug: 'gas-cooker-installation',
    title: 'Gas Cooker & Hob Installation',
    iconName: 'Flame',
    shortDescription:
      'Safe, certified installation of gas hobs, ovens and freestanding cookers.',
    longDescription:
      'We supply and install gas hobs, built-in ovens, and freestanding cookers — including bayonet fittings, rigid pipework, and stability brackets. Every installation is fully tested and certified to current gas regulations.',
    heroImage: IMG_GAS_HOB_NEW,
    gallery: [IMG_GAS_HOB_NEW, IMG_BOILER_REPAIR, IMG_PIPEWORK],
    features: [
      'Hob, oven & range cooker fitting',
      'Bayonet, rigid pipe & flexi connections',
      'Stability bracket fitted',
      'Full safety test & certification',
      'Old appliance removal available',
    ],
  },
  {
    slug: 'power-flushing',
    title: 'Power Flushing',
    iconName: 'Droplets',
    shortDescription:
      'Deep cleaning of your heating system to improve efficiency and longevity.',
    longDescription:
      'Over time, sludge and rust build up inside radiators and pipework, reducing efficiency and causing cold spots. A power flush blasts the system clean — restoring full heat output, lowering bills, and protecting your boiler.',
    heroImage: IMG_CYLINDER,
    gallery: [IMG_CYLINDER, IMG_PIPEWORK, IMG_BOILER_DIAGNOSTIC_2],
    features: [
      'Removes sludge, rust & limescale',
      'Restores radiator heat output',
      'Improves boiler efficiency',
      'Magnetic filter fitted as standard',
      'System inhibitor added on completion',
    ],
  },
]

export function getServiceBySlug(slug: string): Service | undefined {
  return services.find((s) => s.slug === slug)
}
